import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { formatToDDMonYYYY } from './dateUtils'; // Import the date formatting utility
import { handleTrainingFileUpload } from './training_parser'; // Import the training parser
import './DataManagement.css';

const DataManagement = ({ currentUser }) => {
  const canModify = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const [message, setMessage] = useState({ text: '', type: '' });
  const [assets, setAssets] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  // State for date range filtering
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  // State for adding new equipment
  const [showAddEquipmentForm, setShowAddEquipmentForm] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    assetId: '',
    serialNo: '',
    floor: '',
    department: '',
    equipmentName: '',
    manufacturer: '',
    model: '',
    installationDate: '',
    pmDone: '',
    pmDue: '',
    calDone: '',
    calDue: ''
  });
  
  // State for auto-suggestions
  const [suggestions, setSuggestions] = useState({
    equipmentName: [],
    department: [],
    floor: []
  });
  const [activeSuggestionField, setActiveSuggestionField] = useState(null);
  
  // State for training data import
  const [isTrainingImporting, setIsTrainingImporting] = useState(false);

  useEffect(() => {
    loadAllData();
  }, [isTrainingImporting]);

  const parseCollection = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const saveAssetsToServer = async (nextAssets) => {
    const response = await fetch('/api/assets/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assets: nextAssets })
    });

    if (!response.ok) {
      throw new Error(`Failed to save assets (${response.status})`);
    }

    const payload = await response.json();
    const updated = parseCollection(payload);
    setAssets(updated);
    return updated;
  };

  const saveCallLogsToServer = async (nextLogs) => {
    const response = await fetch('/api/call-logs/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs: nextLogs })
    });

    if (!response.ok) {
      throw new Error(`Failed to save call logs (${response.status})`);
    }

    const payload = await response.json();
    const updated = parseCollection(payload);
    setCallLogs(updated);
    setFilteredLogs(updated);
    return updated;
  };

  const loadAllData = async () => {
    try {
      const [assetsResponse, logsResponse] = await Promise.all([
        fetch('/api/assets'),
        fetch('/api/call-logs')
      ]);

      if (!assetsResponse.ok) {
        throw new Error(`Failed to load assets (${assetsResponse.status})`);
      }

      if (!logsResponse.ok) {
        throw new Error(`Failed to load call logs (${logsResponse.status})`);
      }

      const assetsPayload = await assetsResponse.json();
      const logsPayload = await logsResponse.json();
      const allAssets = parseCollection(assetsPayload);
      const logs = parseCollection(logsPayload);

      setAssets(allAssets);
      setCallLogs(logs);
      setFilteredLogs(logs);
        
        // Set default date range to last 30 days
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        
        setDateRange({
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        });
    } catch (error) {
      console.error('Failed to load data:', error);
      setAssets([]);
      setCallLogs([]);
      setFilteredLogs([]);
      showMessage('Failed to load shared server data', 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const cleanAssetValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const normalizeAssetKey = (value) => cleanAssetValue(value).toLowerCase();

  const buildAssetMatchKeys = (asset = {}) => {
    const keys = [];
    const assetId = normalizeAssetKey(asset.assetId);
    const serialNo = normalizeAssetKey(asset.serialNo);

    if (assetId) keys.push(`asset:${assetId}`);
    if (serialNo) keys.push(`serial:${serialNo}`);

    return keys;
  };

  const mergeAssetDetails = (existingAsset = {}, incomingAssetData = {}, fallbackId) => {
    const mergedAsset = {
      ...existingAsset,
      id: existingAsset.id || fallbackId
    };

    Object.entries(incomingAssetData).forEach(([field, value]) => {
      const cleanedValue = cleanAssetValue(value);

      if (cleanedValue !== '') {
        mergedAsset[field] = cleanedValue;
      }
    });

    return mergedAsset;
  };

  // Export condemned equipment to Excel
  const exportCondemnedEquipment = () => {
    const condemnedAssets = assets.filter(asset => asset.status === 'condemned');
    
    if (condemnedAssets.length === 0) {
      showMessage('No condemned equipment found', 'error');
      return;
    }

    const headers = ['S.N', 'Department', 'Floor', 'Equipment', 'Asset ID', 'Manufacturer', 'Model', 'Serial No', 'Installation Date', 'Status Date', 'Remarks'];
    const rows = condemnedAssets.map((asset, index) => [
      index + 1,
      asset.department || '',
      asset.floor || '',
      asset.equipmentName || '',
      asset.assetId || '',
      asset.manufacturer || '',
      asset.model || '',
      asset.serialNo || '',
      asset.installationDate || '',
      asset.statusDate || '',
      asset.statusRemark || ''
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Condemned Equipment');
    XLSX.writeFile(workbook, `condemned_equipment_${new Date().toISOString().split('T')[0]}.xlsx`);
    showMessage(`Exported ${condemnedAssets.length} condemned equipment records`, 'success');
  };

  // Export shifted equipment to Excel
  const exportShiftedEquipment = () => {
    const shiftedAssets = assets.filter(asset => asset.status === 'shifted');
    
    if (shiftedAssets.length === 0) {
      showMessage('No shifted equipment found', 'error');
      return;
    }

    const headers = ['S.N', 'Department', 'Floor', 'Equipment', 'Asset ID', 'Manufacturer', 'Model', 'Serial No', 'Installation Date', 'Status Date', 'Remarks'];
    const rows = shiftedAssets.map((asset, index) => [
      index + 1,
      asset.department || '',
      asset.floor || '',
      asset.equipmentName || '',
      asset.assetId || '',
      asset.manufacturer || '',
      asset.model || '',
      asset.serialNo || '',
      asset.installationDate || '',
      asset.statusDate || '',
      asset.statusRemark || ''
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shifted Equipment');
    XLSX.writeFile(workbook, `shifted_equipment_${new Date().toISOString().split('T')[0]}.xlsx`);
    showMessage(`Exported ${shiftedAssets.length} shifted equipment records`, 'success');
  };

  // Export biomedical equipment to Excel
  const exportBiomedicalEquipment = () => {
    const biomedicalAssets = assets.filter(asset => asset.status === 'biomedical');
    
    if (biomedicalAssets.length === 0) {
      showMessage('No biomedical equipment found', 'error');
      return;
    }

    const headers = ['S.N', 'Department', 'Floor', 'Equipment', 'Asset ID', 'Manufacturer', 'Model', 'Serial No', 'Installation Date', 'Status Date', 'Remarks'];
    const rows = biomedicalAssets.map((asset, index) => [
      index + 1,
      asset.department || '',
      asset.floor || '',
      asset.equipmentName || '',
      asset.assetId || '',
      asset.manufacturer || '',
      asset.model || '',
      asset.serialNo || '',
      asset.installationDate || '',
      asset.statusDate || '',
      asset.statusRemark || ''
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Biomedical Equipment');
    XLSX.writeFile(workbook, `biomedical_equipment_${new Date().toISOString().split('T')[0]}.xlsx`);
    showMessage(`Exported ${biomedicalAssets.length} biomedical equipment records`, 'success');
  };

  // Filter logs by date range
  const filterByDateRange = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setFilteredLogs(callLogs);
      return;
    }
    
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    endDate.setHours(23, 59, 59, 999); // Include the entire end day
    
    const filtered = callLogs.filter(log => {
      const logDate = new Date(log.date);
      return logDate >= startDate && logDate <= endDate;
    });
    
    setFilteredLogs(filtered);
  };

  // Delete a call log entry
  const deleteCallLog = async (id) => {
    console.log('DataManagement: Attempting to delete call log with ID:', id);
    if (window.confirm('Are you sure you want to delete this call log entry? This action cannot be undone.')) {
      try {
        const response = await fetch(`/api/call-logs/${encodeURIComponent(id)}`, {
          method: 'DELETE'
        });
        if (!response.ok) {
          throw new Error(`Failed to delete call log (${response.status})`);
        }

        await loadAllData();
        showMessage('Call log entry deleted successfully!', 'success');
      } catch (error) {
        console.error('DataManagement: Failed to delete call log:', error);
        showMessage('Failed to delete call log entry: ' + error.message, 'error');
      }
    }
  };

  // Delete all call logs with double confirmation
  const deleteAllCallLogs = async () => {
    // First confirmation
    if (window.confirm('Are you sure you want to delete ALL call log entries? This will remove all records.')) {
      // Second confirmation with specific text to type
      const userInput = window.prompt('To confirm deletion of ALL call log entries, please type "DELETE ALL" in the box below:');
      if (userInput && userInput.toUpperCase() === 'DELETE ALL') {
        try {
          await saveCallLogsToServer([]);
          showMessage('All call log entries deleted successfully!', 'success');
        } catch (error) {
          console.error('Failed to delete call logs:', error);
          showMessage('Failed to delete call logs', 'error');
        }
      } else if (userInput !== null) {
        showMessage('Call log deletion cancelled. Text did not match.', 'error');
      }
    }
  };

  // Delete all master assets with double confirmation
  const deleteAllMasterAssets = async () => {
    // First confirmation
    if (window.confirm('Are you sure you want to delete ALL master asset data? This will remove all equipment records.')) {
      // Second confirmation with specific text to type
      const userInput = window.prompt('To confirm deletion of ALL master asset data, please type "DELETE ASSETS" in the box below:');
      if (userInput && userInput.toUpperCase() === 'DELETE ASSETS') {
        try {
          await saveAssetsToServer([]);
          showMessage('All master asset data deleted successfully!', 'success');
        } catch (error) {
          console.error('Failed to delete master assets:', error);
          showMessage('Failed to delete master assets', 'error');
        }
      } else if (userInput !== null) {
        showMessage('Master asset deletion cancelled. Text did not match.', 'error');
      }
    }
  };

  // Toggle Add Equipment Form visibility
  const toggleAddEquipmentForm = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowAddEquipmentForm(prevState => !prevState);
  };

  // Handle input change for new equipment form
  const handleEquipmentInputChange = (e) => {
    const { name, value } = e.target;
    setNewEquipment(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Update suggestions based on input
    if (value.length > 0) {
      const filtered = getUniqueValues(name).filter(item => 
        item.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(prev => ({
        ...prev,
        [name]: filtered.slice(0, 10) // Show max 10 suggestions
      }));
      setActiveSuggestionField(name);
    } else {
      setActiveSuggestionField(null);
    }
  };
  
  // Get unique values from existing assets for a field
  const getUniqueValues = (field) => {
    const values = assets
      .filter(asset => asset && asset[field])
      .map(asset => asset[field])
      .filter((value, index, self) => 
        self.indexOf(value) === index && value.trim() !== ''
      )
      .sort();
    return values;
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (field, value) => {
    setNewEquipment(prev => ({
      ...prev,
      [field]: value
    }));
    setActiveSuggestionField(null);
  };
  
  // Handle input focus to show all suggestions
  const handleInputFocus = (field) => {
    const values = getUniqueValues(field);
    setSuggestions(prev => ({
      ...prev,
      [field]: values.slice(0, 10)
    }));
    setActiveSuggestionField(field);
  };
  
  // Handle input blur to hide suggestions (with delay to allow click)
  const handleInputBlur = (e) => {
    // Don't hide if clicking on a suggestion
    if (e.relatedTarget && e.relatedTarget.classList.contains('suggestion-item')) {
      return;
    }
    setTimeout(() => {
      setActiveSuggestionField(null);
    }, 200);
  };

  // Auto-generate Asset ID based on department and equipment type
  const generateAssetId = () => {
    const { department, equipmentName } = newEquipment;
    
    if (!department || !equipmentName) {
      showMessage('Please enter Department and Equipment Name first', 'error');
      return;
    }

    // Get equipment type abbreviation
    const getEquipmentCode = (name) => {
      const name_upper = name.toUpperCase().trim();
      if (name_upper.includes('SYRINGE')) return 'SP';
      if (name_upper.includes('PUMP')) return 'PUMP';
      if (name_upper.includes('MONITOR')) return 'MON';
      if (name_upper.includes('VENTILATOR')) return 'VENT';
      if (name_upper.includes('DEFIBRILLATOR')) return 'DEFIB';
      if (name_upper.includes('ECG')) return 'ECG';
      if (name_upper.includes('X-RAY')) return 'XRAY';
      if (name_upper.includes('XRAY')) return 'XRAY';
      if (name_upper.includes('ULTRASOUND')) return 'US';
      if (name_upper.includes('DIALYSIS')) return 'DIAL';
      if (name_upper.includes('ANESTHESIA')) return 'ANES';
      if (name_upper.includes('STRETCHER')) return 'STR';
      if (name_upper.includes('TROLLEY')) return 'TROL';
      if (name_upper.includes('BED')) return 'BED';
      if (name_upper.includes('LIGHT')) return 'LIGHT';
      if (name_upper.includes('TABLE')) return 'TABLE';
      if (name_upper.includes('MACHINE')) return 'MACH';
      // Default: take first 2-3 letters of each word
      const words = name.trim().split(/\s+/);
      if (words.length >= 2) {
        return (words[0].substring(0, 2) + words[1].substring(0, 2)).toUpperCase();
      }
      return name.trim().substring(0, 4).toUpperCase();
    };

    // Get department code
    const getDepartmentCode = (dept) => {
      const dept_upper = dept.toUpperCase().trim();
      
      // Extract number and any letter after it (e.g., ICU7D -> 7D, ICU12A -> 12A, 10CD -> 10CD)
      const floorMatch = dept_upper.match(/(\d+[A-Z]?)/);
      const floorNum = floorMatch ? floorMatch[1] : '';
      
      // Check for ER/ERICU first (before ICU to avoid matching ICU in ERICU)
      if (dept_upper.startsWith('ER') || dept_upper.includes('EMERGENCY') || dept_upper === 'ER') return `ER${floorNum}`;
      if (dept_upper.includes('ICU')) return `ICU${floorNum}`;
      if (dept_upper.includes('OT')) return `OT${floorNum}`;
      if (dept_upper.includes('WARD')) return `WARD${floorNum}`;
      if (dept_upper.includes('OPD')) return `OPD`;
      if (dept_upper.includes('CASUALTY')) return `CAS`;
      if (dept_upper.includes('LAB')) return `LAB`;
      if (dept_upper.includes('RADIOLOGY')) return `RAD`;
      if (dept_upper.includes('PHARMACY')) return `PHARM`;
      if (dept_upper.includes('STORE')) return `STORE`;
      if (dept_upper.includes('ADMIN')) return `ADMIN`;
      // Default: return as-is (removing spaces)
      return dept_upper.replace(/\s+/g, '');
    };

    const equipmentCode = getEquipmentCode(equipmentName);
    const departmentCode = getDepartmentCode(department);
    
    // Build the prefix for asset ID - matching existing format ZHL/AHD/BME/DEPT/EQUIP/
    const prefix = `ZHL/AHD/BME/${departmentCode}/${equipmentCode}/`;
    
    console.log('Generating Asset ID:');
    console.log('  Department Input:', department);
    console.log('  Department Code:', departmentCode);
    console.log('  Equipment Input:', equipmentName);
    console.log('  Equipment Code:', equipmentCode);
    console.log('  Asset ID Prefix:', prefix);
    console.log('  Total assets in database:', assets.length);
    
    // Normalize inputs for matching
    const normalizedDept = department.toUpperCase().trim().replace(/\s+/g, '');
    const normalizedEquip = equipmentName.toUpperCase().trim();
    
    console.log('  Normalized Department:', normalizedDept);
    console.log('  Normalized Equipment:', normalizedEquip);
    
    // Find all assets in the same department with same equipment name
    const sameDeptAndEquipment = assets.filter(asset => {
      if (!asset || !asset.department || !asset.equipmentName) {
        console.log('    Skipping asset - missing data:', asset);
        return false;
      }
      
      const assetDept = asset.department.toUpperCase().trim().replace(/\s+/g, '');
      const assetEquip = asset.equipmentName.toUpperCase().trim();
      
      // Check if department matches (exact or contains)
      const deptMatches = assetDept === normalizedDept || 
                         assetDept.includes(normalizedDept) || 
                         normalizedDept.includes(assetDept);
      
      // Check if equipment name matches (exact or contains)
      const equipMatches = assetEquip === normalizedEquip || 
                          assetEquip.includes(normalizedEquip) || 
                          normalizedEquip.includes(assetEquip);
      
      const isMatch = deptMatches && equipMatches;
      
      if (isMatch) {
        console.log('    ✓ Match found:', asset.assetId, '| Dept:', asset.department, '| Equip:', asset.equipmentName);
      }
      
      return isMatch;
    });
    
    console.log('  Same Department & Equipment count:', sameDeptAndEquipment.length);
    
    // Find the highest sequential number from existing asset IDs with same pattern
    let maxNumber = 0;
    sameDeptAndEquipment.forEach(asset => {
      if (asset.assetId) {
        // Extract the number at the end of the asset ID (e.g., ZHL/AHD/BME/ICU7D/SP/10 -> 10)
        const match = asset.assetId.match(/\/(\d+)$/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) {
            maxNumber = num;
          }
        }
      }
    });
    
    console.log('  Highest existing number found:', maxNumber);
    
    // Get the next sequential number (always 2 digits: 01, 02, 03...)
    const nextNumber = maxNumber + 1;
    const nextNumberFormatted = nextNumber.toString().padStart(2, '0');
    
    console.log('  Next number:', nextNumberFormatted);
    
    // Generate the asset ID
    const generatedAssetId = `${prefix}${nextNumberFormatted}`;
    
    // Update the form
    setNewEquipment(prev => ({
      ...prev,
      assetId: generatedAssetId
    }));
    
    showMessage(`Asset ID generated: ${generatedAssetId} (${count} existing + 1 new)`, 'success');
  };

  // Add new equipment to master database
  const addNewEquipment = async (e) => {
    e.preventDefault();
    
    // Remove the mandatory field validation
    // Just check if at least one field is filled
    const hasAnyField = newEquipment.assetId || newEquipment.serialNo || 
        newEquipment.floor || newEquipment.department || 
        newEquipment.equipmentName || newEquipment.manufacturer || 
        newEquipment.model;
        
    if (!hasAnyField) {
      showMessage('Please fill in at least one field', 'error');
      return;
    }
    
    try {
      const normalizedNewAssetId = normalizeAssetKey(newEquipment.assetId);
      const normalizedNewSerialNo = normalizeAssetKey(newEquipment.serialNo);
      const existingAsset = assets.find(asset => {
        const matchesAssetId = normalizedNewAssetId && normalizeAssetKey(asset.assetId) === normalizedNewAssetId;
        const matchesSerialNo = normalizedNewSerialNo && normalizeAssetKey(asset.serialNo) === normalizedNewSerialNo;

        return matchesAssetId || matchesSerialNo;
      });

      const cleanedEquipmentData = Object.fromEntries(
        Object.entries(newEquipment).map(([field, value]) => [field, cleanAssetValue(value)])
      );

      const updatedAssets = existingAsset
        ? assets.map(asset => (
            asset.id === existingAsset.id
              ? mergeAssetDetails(existingAsset, cleanedEquipmentData, existingAsset.id || Date.now().toString())
              : asset
          ))
        : [
            ...assets,
            {
              id: Date.now().toString(),
              ...cleanedEquipmentData
            }
          ];
      
      await saveAssetsToServer(updatedAssets);
      
      // Reset form
      setNewEquipment({
        assetId: '',
        serialNo: '',
        floor: '',
        department: '',
        equipmentName: '',
        manufacturer: '',
        model: '',
        installationDate: '',
        pmDone: '',
        pmDue: '',
        calDone: '',
        calDue: ''
      });
      
      // Hide form
      setShowAddEquipmentForm(false);
      
      showMessage(
        existingAsset
          ? 'Equipment details updated on the existing asset. Uploaded data stayed mapped correctly.'
          : 'Equipment added successfully to master database!',
        'success'
      );
    } catch (error) {
      console.error('Failed to add equipment:', error);
      showMessage('Failed to add equipment. Please try again.', 'error');
    }
  };

  // Clear equipment form
  const clearEquipmentForm = () => {
    setNewEquipment({
      assetId: '',
      serialNo: '',
      floor: '',
      department: '',
      equipmentName: '',
      manufacturer: '',
      model: '',
      installationDate: '',
      pmDone: '',
      pmDue: '',
      calDone: '',
      calDue: ''
    });
    setShowAddEquipmentForm(false);
  };

  // Handle Master Asset Data Import
  const handleAssetImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Use current server-loaded assets to preserve reports and history
        const existingAssets = Array.isArray(assets) ? assets : [];
        
        // Create a map of existing assets by normalized assetId and serialNo for quick lookup
        const existingAssetsMap = new Map();
        existingAssets.forEach(asset => {
          buildAssetMatchKeys(asset).forEach(key => {
            if (!existingAssetsMap.has(key)) {
              existingAssetsMap.set(key, asset);
            }
          });
        });

        const matchedExistingAssetIds = new Set();
        
        // Process the data to match our asset structure
        const importedAssets = jsonData.map((row, index) => {
          const assetId = cleanAssetValue(row['Asset ID']);
          const serialNo = cleanAssetValue(row['Serial No'] || row['Serial Number']);
          
          // Try to find existing asset by Asset ID or Serial Number
          const existingAsset = buildAssetMatchKeys({ assetId, serialNo })
            .map(key => existingAssetsMap.get(key))
            .find(Boolean);
          
          // Create new asset from Excel data
          const newAssetData = {
            equipmentName: cleanAssetValue(row['Equipment Name'] || row['Equipments'] || row['Equipment']),
            manufacturer: cleanAssetValue(row['Manufacturer/Company'] || row['Manufacturer'] || row['Company']),
            model: cleanAssetValue(row['Model']),
            assetId,
            serialNo,
            floor: cleanAssetValue(row['Floor']),
            department: cleanAssetValue(row['Department']),
            installationDate: cleanAssetValue(row['Installation Date']),
            pmDue: cleanAssetValue(row['PM DUE']),
            pmDone: cleanAssetValue(row['PM DONE']),
            calDue: cleanAssetValue(row['CAL DUE']),
            calDone: cleanAssetValue(row['CAL DONE']),
            type: cleanAssetValue(row['Type'])
          };
          
          if (existingAsset) {
            if (existingAsset.id !== undefined && existingAsset.id !== null) {
              matchedExistingAssetIds.add(existingAsset.id);
            }

            return mergeAssetDetails(existingAsset, newAssetData, existingAsset.id || `${Date.now()}-${index}`);
          }

          return {
            id: `${Date.now()}-${index}`,
            ...newAssetData
          };
        }).filter(asset => asset.assetId || asset.serialNo); // Filter out empty rows

        const importedAssetKeys = new Set();
        importedAssets.forEach(asset => {
          buildAssetMatchKeys(asset).forEach(key => importedAssetKeys.add(key));
        });

        const preservedExistingAssets = existingAssets.filter(asset => {
          if (matchedExistingAssetIds.has(asset.id)) {
            return false;
          }

          const existingKeys = buildAssetMatchKeys(asset);
          if (existingKeys.length === 0) {
            return true;
          }

          return !existingKeys.some(key => importedAssetKeys.has(key));
        });

        const processedAssets = [...importedAssets, ...preservedExistingAssets];
        
        if (processedAssets.length === 0) {
          throw new Error('No valid assets found in the uploaded file');
        }
        
        // Count how many assets had reports preserved
        const assetsWithReports = processedAssets.filter(asset => 
          asset.pmReportData || asset.calReportData || 
          (asset.pmReportHistory && asset.pmReportHistory.length > 0) ||
          (asset.calReportHistory && asset.calReportHistory.length > 0)
        ).length;
        
        await saveAssetsToServer(processedAssets);
        
        // Show success message with report preservation info
        let message = `Successfully imported ${importedAssets.length} assets from file!`;
        if (assetsWithReports > 0) {
          message += ` Reports preserved for ${assetsWithReports} equipment.`;
        }
        if (preservedExistingAssets.length > 0) {
          message += ` Kept ${preservedExistingAssets.length} existing equipment records that were not in the file.`;
        }
        showMessage(message, 'success');
      } catch (error) {
        console.error('Failed to import assets:', error);
        showMessage(`Error importing file: ${error.message}`, 'error');
      } finally {
        setIsImporting(false);
        // Reset file input
        event.target.value = '';
      }
    };
    
    reader.onerror = () => {
      showMessage('Failed to read file', 'error');
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    };
    
    reader.readAsArrayBuffer(file);
  };

  // Handle Call Log Preview & Edit Import
  const handleCallLogImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Process the data to match our call log structure
        const processedLogs = jsonData.map((row, index) => ({
          id: Date.now() + index, // Simple ID generation
          date: row['Date'] || '',
          assetId: row['Asset ID'] || '',
          serialNo: row['Serial Number'] || '',
          equipmentName: row['Equipment'] || '',
          company: row['Manufacturer'] || '',
          model: row['Model'] || '',
          department: row['Department'] || '',
          floor: row['Floor'] || '',
          attendant: row['Attendant'] || '',
          natureOfComplaint: row['Nature Of Complaint'] || '',
          actionTaken: row['Action Taken'] || '',
          startTime: row['Call Time'] || '',
          endTime: row['End Time'] || '',
          callAttendTime: row['Call Attend Time'] || '',
          responseTime: row['Response Time'] || '',
          resolutionTime: row['Resolution Time (H:MM)'] || '',
          type: row['Type'] || 'BME',
          attendedBy: row['Att. By'] || 'INHOUSE',
          callEndDate: row['Call End Date'] || '',
          sparesUsed: row['Spares Used'] || '',
          sparesCost: row['Spares Cost'] || '',
          remarks: row['Remarks'] || '',
          callStatus: row['Call Status'] || '',
          snN: row['SN.N'] || '',
          userName: row['User Name'] || '',
          userRole: row['User Role'] || '',
          createdAt: row['Created At'] || new Date().toISOString(),
        })).filter(log => log.assetId || log.serialNo); // Filter out empty rows
        
        if (processedLogs.length === 0) {
          throw new Error('No valid call logs found in the uploaded file');
        }
        
        await saveCallLogsToServer(processedLogs);
        showMessage(`Successfully imported ${processedLogs.length} call logs from file!`, 'success');
      } catch (error) {
        console.error('Failed to import call logs:', error);
        showMessage(`Error importing file: ${error.message}`, 'error');
      } finally {
        setIsImporting(false);
        // Reset file input
        event.target.value = '';
      }
    };
    
    reader.onerror = () => {
      showMessage('Failed to read file', 'error');
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    };
    
    reader.readAsArrayBuffer(file);
  };

  // Handle Training Data Import
  const handleTrainingImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsTrainingImporting(true);
    
    handleTrainingFileUpload(file, assets)
      .then(async (updatedAssets) => {
        await saveAssetsToServer(updatedAssets);
        showMessage(`Successfully imported training data for ${updatedAssets.length} assets from file!`, 'success');
      })
      .catch(error => {
        console.error('Failed to import training data:', error);
        showMessage(`Error importing training data: ${error.message}`, 'error');
      })
      .finally(() => {
        setIsTrainingImporting(false);
        // Reset file input
        event.target.value = '';
      });
  };

  // Export Master Asset Data
  const exportAssetsToExcel = () => {
    try {
      if (assets.length === 0) {
        showMessage('No assets to export. Database is empty.', 'error');
        return;
      }
      
      // Create worksheet data with formatted dates
      const worksheetData = [
        ['Equipment Name', 'Manufacturer/Company', 'Model', 'Asset ID', 'Serial No', 'Floor', 'Department', 'Installation Date', 'PM DUE', 'PM DONE', 'CAL DUE', 'CAL DONE', 'Type'],
        ...assets.map(asset => [
          asset.equipmentName || '',
          asset.manufacturer || '',
          asset.model || '',
          asset.assetId || '',
          asset.serialNo || '',
          asset.floor || '',
          asset.department || '',
          formatToDDMonYYYY(asset.installationDate) || asset.installationDate || '', // Format date for export
          formatToDDMonYYYY(asset.pmDue) || asset.pmDue || '',           // Format date for export
          formatToDDMonYYYY(asset.pmDone) || asset.pmDone || '',          // Format date for export
          formatToDDMonYYYY(asset.calDue) || asset.calDue || '',          // Format date for export
          formatToDDMonYYYY(asset.calDone) || asset.calDone || '',         // Format date for export
          asset.type || ''
        ])
      ];
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Master Assets');
      
      // Export to Excel file
      XLSX.writeFile(wb, `master_assets_${new Date().toISOString().slice(0, 10)}.xlsx`);
      
      showMessage('Master Asset Data exported successfully as Excel file!', 'success');
    } catch (error) {
      console.error('Failed to export assets:', error);
      showMessage('Failed to export Master Asset Data', 'error');
    }
  };

  // Export call logs to CSV
  const exportCallLogsToCSV = () => {
    if (callLogs.length === 0) {
      showMessage('No data to export. Database is empty.', 'error');
      return;
    }
    
    try {
      // Define CSV headers with all fields in proper order
      const headers = [
        'Date', 'Asset ID', 'Serial Number', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map call logs to CSV rows with all fields in proper order
      // Using formatToDDMonYYYY for dates to match table display format
      const rows = callLogs.map(log => [
        formatToDDMonYYYY(log.date) || '',
        log.assetId || '',
        log.serialNo || '',
        log.equipmentName || '',
        log.company || '',
        log.model || '',
        log.department || '',
        log.floor || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        formatToDDMonYYYY(log.callEndDate) || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      ].join('\r\n');
      
      // Create download link with proper CSV MIME type
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `call_logs_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showMessage(`Successfully exported ${callLogs.length} records to CSV!`, 'success');
    } catch (error) {
      console.error('Failed to export call logs:', error);
      showMessage('Failed to export call logs. Please try again.', 'error');
    }
  };

  // Export call logs to Excel format
  const exportCallLogsToExcel = () => {
    if (callLogs.length === 0) {
      showMessage('No data to export. Database is empty.', 'error');
      return;
    }
    
    try {
      // Define Excel headers with all fields in proper order
      const headers = [
        'Date', 'Asset ID', 'Serial Number', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map call logs to rows with all fields in proper order
      // Using formatToDDMonYYYY for dates to match table display format
      const rows = callLogs.map(log => [
        formatToDDMonYYYY(log.date) || '',
        log.assetId || '',
        log.serialNo || '',
        log.equipmentName || '',
        log.company || '',
        log.model || '',
        log.department || '',
        log.floor || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        formatToDDMonYYYY(log.callEndDate) || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Create worksheet data
      const worksheetData = [headers, ...rows];
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Call Logs');
      
      // Export to Excel file
      XLSX.writeFile(wb, `call_logs_${new Date().toISOString().slice(0, 10)}.xlsx`);
      
      showMessage(`Successfully exported ${callLogs.length} records to Excel format!`, 'success');
    } catch (error) {
      console.error('Failed to export call logs to Excel:', error);
      showMessage('Failed to export call logs to Excel. Please try again.', 'error');
    }
  };

  // Export call logs to PDF (simplified CSV approach)
  const exportCallLogsToPDF = () => {
    if (callLogs.length === 0) {
      showMessage('No data to export. Database is empty.', 'error');
      return;
    }
    
    try {
      // Define PDF headers with all fields in proper order
      const headers = [
        'Date', 'Asset ID', 'Serial Number', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map call logs to rows with all fields in proper order
      // Using formatToDDMonYYYY for dates to match table display format
      const rows = callLogs.map(log => [
        formatToDDMonYYYY(log.date) || '',
        log.assetId || '',
        log.serialNo || '',
        log.equipmentName || '',
        log.company || '',
        log.model || '',
        log.department || '',
        log.floor || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        formatToDDMonYYYY(log.callEndDate) || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Create worksheet data
      const worksheetData = [headers, ...rows];
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Call Logs');
      
      // Export to PDF file (using Excel format as base)
      XLSX.writeFile(wb, `call_logs_${new Date().toISOString().slice(0, 10)}.pdf`);
      
      showMessage(`Successfully exported ${callLogs.length} records to PDF format!`, 'success');
    } catch (error) {
      console.error('Failed to export call logs to PDF:', error);
      showMessage('Failed to export call logs to PDF. Please try again.', 'error');
    }
  };

  // Export filtered call logs to CSV
  const exportFilteredCallLogsToCSV = () => {
    if (filteredLogs.length === 0) {
      showMessage('No filtered data to export. Please apply filters or check your data.', 'error');
      return;
    }
    
    try {
      // Define CSV headers with all fields in proper order
      const headers = [
        'Date', 'Asset ID', 'Serial Number', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map filtered call logs to CSV rows with all fields in proper order
      // Using formatToDDMonYYYY for dates to match table display format
      const rows = filteredLogs.map(log => [
        formatToDDMonYYYY(log.date) || '',
        log.assetId || '',
        log.serialNo || '',
        log.equipmentName || '',
        log.company || '',
        log.model || '',
        log.department || '',
        log.floor || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        formatToDDMonYYYY(log.callEndDate) || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      ].join('\r\n');
      
      // Create download link with proper CSV MIME type
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `filtered_call_logs_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showMessage(`Successfully exported ${filteredLogs.length} filtered records to CSV!`, 'success');
    } catch (error) {
      console.error('Failed to export filtered call logs:', error);
      showMessage('Failed to export filtered call logs. Please try again.', 'error');
    }
  };

  // Export filtered call logs to Excel format
  const exportFilteredCallLogsToExcel = () => {
    if (filteredLogs.length === 0) {
      showMessage('No filtered data to export. Please apply filters or check your data.', 'error');
      return;
    }
    
    try {
      // Define Excel headers with all fields in proper order
      const headers = [
        'Date', 'Asset ID', 'Serial Number', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map filtered call logs to rows with all fields in proper order
      // Using formatToDDMonYYYY for dates to match table display format
      const rows = filteredLogs.map(log => [
        formatToDDMonYYYY(log.date) || '',
        log.assetId || '',
        log.serialNo || '',
        log.equipmentName || '',
        log.company || '',
        log.model || '',
        log.department || '',
        log.floor || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        formatToDDMonYYYY(log.callEndDate) || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Create worksheet data
      const worksheetData = [headers, ...rows];
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Filtered Call Logs');
      
      // Export to Excel file
      XLSX.writeFile(wb, `filtered_call_logs_${new Date().toISOString().slice(0, 10)}.xlsx`);
      
      showMessage(`Successfully exported ${filteredLogs.length} filtered records to Excel format!`, 'success');
    } catch (error) {
      console.error('Failed to export filtered call logs to Excel:', error);
      showMessage('Failed to export filtered call logs to Excel. Please try again.', 'error');
    }
  };

  // Export filtered call logs to PDF (simplified CSV approach)
  const exportFilteredCallLogsToPDF = () => {
    if (filteredLogs.length === 0) {
      showMessage('No filtered data to export. Please apply filters or check your data.', 'error');
      return;
    }
    
    try {
      // Define PDF headers with all fields in proper order
      const headers = [
        'Date', 'Asset ID', 'Serial Number', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map filtered call logs to rows with all fields in proper order
      // Using formatToDDMonYYYY for dates to match table display format
      const rows = filteredLogs.map(log => [
        formatToDDMonYYYY(log.date) || '',
        log.assetId || '',
        log.serialNo || '',
        log.equipmentName || '',
        log.company || '',
        log.model || '',
        log.department || '',
        log.floor || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        formatToDDMonYYYY(log.callEndDate) || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Create worksheet data
      const worksheetData = [headers, ...rows];
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Filtered Call Logs');
      
      // Export to PDF file (using Excel format as base)
      XLSX.writeFile(wb, `filtered_call_logs_${new Date().toISOString().slice(0, 10)}.pdf`);
      
      showMessage(`Successfully exported ${filteredLogs.length} filtered records to PDF format!`, 'success');
    } catch (error) {
      console.error('Failed to export filtered call logs to PDF:', error);
      showMessage('Failed to export filtered call logs to PDF. Please try again.', 'error');
    }
  };

  return (
    <div className="data-management-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">🔄 Data Import/Export</h2>
        </div>
        
        {currentUser && (
          <div className="user-badge">
            👤 Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}
        
        <div className="data-sections">
          {/* Master Asset Data Section */}
          <div className="data-section master-asset-section">
            <h3>🗄️ Master Asset Data</h3>
            <div className="data-controls">
              <div className="import-section">
                <h4>Import Master Asset Data</h4>
                <div className="file-upload-area">
                  <input 
                    type="file" 
                    accept=".csv,.xlsx,.xls" 
                    onChange={handleAssetImport} 
                    disabled={isImporting || !canModify}
                    id="import-assets" 
                  />
                  <label htmlFor="import-assets" className="btn btn-primary">
                    {isImporting ? ' Importing...' : '📁 Choose Excel File'}
                  </label>
                  <p><small>Upload an Excel file with equipment data to update the master database</small></p>
                </div>
              </div>
              
              <div className="export-section">
                <h4>Export Master Asset Data</h4>
                <div className="export-buttons">
                  <button className="btn btn-success" onClick={exportAssetsToExcel}>
                    📤 Export to Excel
                  </button>
                </div>
                <p><small>Download the current master asset database as an Excel file</small></p>
              </div>
              
              <div className="delete-section">
                <h4>Delete Master Asset Data</h4>
                <div className="delete-buttons">
                  <button className="btn btn-danger" onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteAllMasterAssets(); }}>
                    🗑️ Delete All Master Assets
                  </button>
                </div>
                <p><small>Delete all equipment records from the master database</small></p>
              </div>
              
              <div className="database-info">
                <h4>Database Information</h4>
                <div className="stats-grid">
                  <div className="stat-card">
                    <h5>Total Assets</h5>
                    <div className="stat-value">{assets.length}</div>
                  </div>
                </div>
              </div>
              
              {/* Training Data Import Section */}
              <div className="import-section">
                <h4>Import Training Data</h4>
                <div className="file-upload-area">
                  <input 
                    type="file" 
                    accept=".xlsx,.xls" 
                    onChange={handleTrainingImport} 
                    disabled={isTrainingImporting}
                    id="import-training" 
                  />
                  <label htmlFor="import-training" className="btn btn-primary">
                    {isTrainingImporting ? ' Importing...' : '📁 Choose Training Excel File'}
                  </label>
                  <p><small>Upload an Excel file with training data to update asset records</small></p>
                  <p><small><a href="/training_data_template.csv" download>📥 Download Training Data Template (CSV)</a></small></p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Add New Equipment Section */}
          <div className="data-section add-equipment-section">
            <h3>🛠️ Add New Equipment to Database</h3>
            <div className="data-controls">
              <div className="add-equipment-controls">
                <button 
                  type="button"
                  className="btn btn-primary"
                  onClick={toggleAddEquipmentForm}
                  disabled={!canModify}
                >
                  {showAddEquipmentForm ? 'Cancel' : '➕ Add Equipment'}
                </button>
                
                {showAddEquipmentForm && (
                  <div className="add-equipment-form">
                    <h4>Enter Equipment Details</h4>
                    <form onSubmit={addNewEquipment}>
                      <div className="form-row">
                        <div className="form-col">
                          <div className="form-group suggestion-group">
                            <label htmlFor="equipmentName">🔧 Equipment Name</label>
                            <input
                              type="text"
                              id="equipmentName"
                              name="equipmentName"
                              value={newEquipment.equipmentName}
                              onChange={handleEquipmentInputChange}
                              onFocus={() => handleInputFocus('equipmentName')}
                              onBlur={handleInputBlur}
                              placeholder="Enter equipment name"
                              autoComplete="off"
                            />
                            {activeSuggestionField === 'equipmentName' && suggestions.equipmentName.length > 0 && (
                              <ul className="suggestion-list">
                                {suggestions.equipmentName.map((suggestion, index) => (
                                  <li 
                                    key={index} 
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleSuggestionClick('equipmentName', suggestion);
                                    }}
                                    className="suggestion-item"
                                    tabIndex="-1"
                                  >
                                    {suggestion}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="manufacturer">🏢 Manufacturer/Company</label>
                            <input
                              type="text"
                              id="manufacturer"
                              name="manufacturer"
                              value={newEquipment.manufacturer}
                              onChange={handleEquipmentInputChange}
                              placeholder="Enter manufacturer/company"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="model">📱 Model</label>
                            <input
                              type="text"
                              id="model"
                              name="model"
                              value={newEquipment.model}
                              onChange={handleEquipmentInputChange}
                              placeholder="Enter model"
                            />
                          </div>
                        </div>
                        
                        <div className="form-col">
                          <div className="form-group suggestion-group">
                            <label htmlFor="department">🏥 Department</label>
                            <input
                              type="text"
                              id="department"
                              name="department"
                              value={newEquipment.department}
                              onChange={handleEquipmentInputChange}
                              onFocus={() => handleInputFocus('department')}
                              onBlur={handleInputBlur}
                              placeholder="Enter department"
                              autoComplete="off"
                            />
                            {activeSuggestionField === 'department' && suggestions.department.length > 0 && (
                              <ul className="suggestion-list">
                                {suggestions.department.map((suggestion, index) => (
                                  <li 
                                    key={index} 
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleSuggestionClick('department', suggestion);
                                    }}
                                    className="suggestion-item"
                                    tabIndex="-1"
                                  >
                                    {suggestion}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-col">
                          <div className="form-group suggestion-group">
                            <label htmlFor="floor">🏢 Floor</label>
                            <input
                              type="text"
                              id="floor"
                              name="floor"
                              value={newEquipment.floor}
                              onChange={handleEquipmentInputChange}
                              onFocus={() => handleInputFocus('floor')}
                              onBlur={handleInputBlur}
                              placeholder="Enter floor"
                              autoComplete="off"
                            />
                            {activeSuggestionField === 'floor' && suggestions.floor.length > 0 && (
                              <ul className="suggestion-list">
                                {suggestions.floor.map((suggestion, index) => (
                                  <li 
                                    key={index} 
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleSuggestionClick('floor', suggestion);
                                    }}
                                    className="suggestion-item"
                                    tabIndex="-1"
                                  >
                                    {suggestion}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        
                        <div className="form-col form-col-full">
                          <div className="form-group">
                            <label htmlFor="assetId">🆔 Asset ID</label>
                            <div className="asset-id-input-group">
                              <input
                                type="text"
                                id="assetId"
                                name="assetId"
                                value={newEquipment.assetId}
                                onChange={handleEquipmentInputChange}
                                placeholder="Enter asset ID or generate"
                                className="asset-id-input"
                              />
                              <button
                                type="button"
                                className="btn-generate-id"
                                onClick={generateAssetId}
                                title="Auto-generate Asset ID based on Department and Equipment"
                              >
                                🔄 Generate
                              </button>
                            </div>
                            <small className="field-hint">
                              Format: ZHL/AHD/BME/DEPT/EQUIP/NUMBER
                            </small>
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="serialNo">🔢 Serial Number</label>
                            <input
                              type="text"
                              id="serialNo"
                              name="serialNo"
                              value={newEquipment.serialNo}
                              onChange={handleEquipmentInputChange}
                              placeholder="Enter serial number"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="installationDate">📅 Installation Date</label>
                            <input
                              type="date"
                              id="installationDate"
                              name="installationDate"
                              value={newEquipment.installationDate}
                              onChange={handleEquipmentInputChange}
                            />
                          </div>
                        </div>
                        
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="pmDone">✅ PM Done Date</label>
                            <input
                              type="date"
                              id="pmDone"
                              name="pmDone"
                              value={newEquipment.pmDone}
                              onChange={handleEquipmentInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="pmDue">⏰ PM Due Date</label>
                            <input
                              type="date"
                              id="pmDue"
                              name="pmDue"
                              value={newEquipment.pmDue}
                              onChange={handleEquipmentInputChange}
                            />
                          </div>
                        </div>
                        
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="calDone">✅ Calibration Done Date</label>
                            <input
                              type="date"
                              id="calDone"
                              name="calDone"
                              value={newEquipment.calDone}
                              onChange={handleEquipmentInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-col">
                          <div className="form-group">
                            <label htmlFor="calDue">⏰ Calibration Due Date</label>
                            <input
                              type="date"
                              id="calDue"
                              name="calDue"
                              value={newEquipment.calDue}
                              onChange={handleEquipmentInputChange}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-actions">
                        <button type="submit" className="btn btn-success">
                          ✅ Add to Database
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={clearEquipmentForm}
                        >
                          🗑️ Clear
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Call Log Preview & Edit Section */}
          <div className="data-section call-service-section">
            <h3>📋 Call Log Preview & Edit</h3>
            
            {/* Import Section */}
            <div className="data-controls">
              <div className="import-section">
                <h4>📥 Import Call Log Data</h4>
                <div className="file-upload-area">
                  <input 
                    type="file" 
                    accept=".csv,.xlsx,.xls" 
                    onChange={handleCallLogImport} 
                    disabled={isImporting}
                    id="import-call-logs" 
                  />
                  <label htmlFor="import-call-logs" className="btn btn-primary">
                    {isImporting ? ' Importing...' : '📁 Choose Excel File'}
                  </label>
                  <p><small>Upload an Excel file with call log data</small></p>
                </div>
              </div>
              
              <div className="database-info">
                <h4>📊 Call Log Database Information</h4>
                <div className="stats-grid">
                  <div className="stat-card">
                    <h5>Total Call Logs</h5>
                    <div className="stat-value">{callLogs.length}</div>
                  </div>
                  <div className="stat-card">
                    <h5>Filtered Logs</h5>
                    <div className="stat-value">{filteredLogs.length}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Filter by Date Range Section */}
            <div className="data-controls filter-controls">
              <div className="filter-section">
                <h4>📅 Filter Call Logs by Date Range</h4>
                <div className="form-row">
                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="startDate">📅 Start Date</label>
                      <input
                        type="date"
                        id="startDate"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="endDate">📅 End Date</label>
                      <input
                        type="date"
                        id="endDate"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div className="form-col">
                    <div className="form-group">
                      <label>&nbsp;</label>
                      <button className="btn btn-primary" onClick={filterByDateRange}>
                        🔍 Apply Filter
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="stats-info">
                  <p>Total Call Logs: {callLogs.length} | Filtered Logs: {filteredLogs.length}</p>
                </div>
              </div>
            </div>
            
            {/* Export Section - All Data */}
            <div className="data-controls export-controls">
              <div className="export-section">
                <h4>💾 Export All Call Logs</h4>
                <div className="export-buttons">
                  <button className="btn btn-info" onClick={exportCallLogsToCSV}>
                    📄 CSV
                  </button>
                  <button className="btn btn-success" onClick={exportCallLogsToExcel}>
                    📊 Excel
                  </button>
                  <button className="btn btn-warning" onClick={exportCallLogsToPDF}>
                    📎 PDF
                  </button>
                </div>
                <p><small>Download all call logs in various formats</small></p>
              </div>
              
              {/* Export Section - Filtered Data */}
              <div className="export-section">
                <h4>💾 Export Filtered Call Logs</h4>
                <div className="export-buttons">
                  <button className="btn btn-info" onClick={exportFilteredCallLogsToCSV}>
                    📄 CSV
                  </button>
                  <button className="btn btn-success" onClick={exportFilteredCallLogsToExcel}>
                    📊 Excel
                  </button>
                  <button className="btn btn-warning" onClick={exportFilteredCallLogsToPDF}>
                    📎 PDF
                  </button>
                </div>
                <p><small>Download only the filtered call logs based on your date range</small></p>
              </div>
            </div>
          </div>
          
          {/* Equipment Status Export - Compact Section (Last in page) */}
          <div className="data-section equipment-status-section" style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: '2px solid #0ea5e9',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginTop: '1rem'
          }}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem'}}>
              <div>
                <h4 style={{margin: '0 0 0.25rem 0', color: '#0369a1', fontSize: '1rem'}}>📦 Equipment Status Export</h4>
                <p style={{margin: 0, color: '#64748b', fontSize: '0.875rem'}}>Download marked equipment lists</p>
              </div>
              <div style={{position: 'relative'}}>
                <select 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'condemned') exportCondemnedEquipment();
                    if (value === 'shifted') exportShiftedEquipment();
                    if (value === 'biomedical') exportBiomedicalEquipment();
                    e.target.value = '';
                  }}
                  style={{
                    padding: '0.5rem 2rem 0.5rem 1rem',
                    borderRadius: '0.375rem',
                    border: '2px solid #0ea5e9',
                    backgroundColor: '#0284c7',
                    color: 'white',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    minWidth: '180px'
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>📥 Select Export...</option>
                  <option value="condemned">🗑️ Condemned Equipment</option>
                  <option value="shifted">📦 Shifted Equipment</option>
                  <option value="biomedical">🔬 Biomedical Equipment</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataManagement;